import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Play, BookOpen, Clock } from "lucide-react";
import Hero from "@/components/Hero";
import { useAuth } from "@/contexts/AuthContext";
import { getUserCourses } from "@/lib/user-courses";
import { getLesson } from "@/lib/courses";
import { toast } from "@/components/ui/use-toast";
import { UserCourse } from "@/types/supabase";

interface EnrolledCourse extends UserCourse {
  last_lesson_title?: string;
}

const MyCourses = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [enrolledCourses, setEnrolledCourses] = useState<EnrolledCourse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserCourses = async () => {
      if (!user) {
        if (!authLoading) {
          toast({
            title: "Sisselogimine vajalik",
            description: "Palun logi sisse, et näha oma kursuseid",
            variant: "destructive"
          });
          navigate("/courses");
        }
        return;
      }
      
      try {
        setLoading(true);
        const userCourses = await getUserCourses(user.id);
        
        // For debugging
        console.log("Fetched user courses:", userCourses);
        
        const coursesWithLessonTitles = await Promise.all(
          userCourses.map(async (course) => {
            if (course.last_lesson_id) {
              try {
                const lesson = await getLesson(course.last_lesson_id);
                return {
                  ...course,
                  last_lesson_title: lesson?.title || "Esimene tund"
                };
              } catch (error) {
                console.error(`Error fetching lesson for course ${course.course_id}:`, error);
                return {
                  ...course,
                  last_lesson_title: "Esimene tund"
                };
              }
            }
            return {
              ...course,
              last_lesson_title: "Esimene tund"
            };
          })
        );
        
        setEnrolledCourses(coursesWithLessonTitles);
      } catch (error) {
        console.error("Error fetching user courses:", error);
        toast({
          title: "Viga",
          description: "Kursuste laadimine ebaõnnestus. Palun proovi uuesti.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUserCourses();
  }, [user, navigate, authLoading]);
  
  const handleContinueCourse = (courseId: string) => {
    console.log("Navigating to course:", courseId);
    navigate(`/learner/course/${courseId}`);
  };
  
  return (
    <div className="fade-in">
      <Hero 
        title="Minu kursused"
        subtitle="Jätka oma õppimist sealt, kus pooleli jäi"
        bgColor="bg-gradient-to-r from-kaija-light to-kaija-sage"
      />
      
      <section className="section-padding bg-white">
        <div className="container-custom">
          {loading || authLoading ? (
            <div className="flex items-center justify-center h-60">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kaija-accent"></div>
            </div>
          ) : enrolledCourses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {enrolledCourses.map((enrollment) => (
                <Card key={enrollment.id} className="overflow-hidden flex flex-col h-full">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={enrollment.course?.image_url} 
                      alt={enrollment.course?.title} 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                      <Button 
                        asChild
                        className="bg-white text-kaija-accent hover:bg-gray-100"
                      >
                        <Link to={`/course/${enrollment.course_id}/lesson/${enrollment.last_lesson_id}`}>
                          <Play className="mr-2 h-4 w-4" /> Jätka vaatamist
                        </Link>
                      </Button>
                    </div>
                  </div>
                  
                  <CardHeader>
                    <CardTitle className="text-xl text-kaija-accent">{enrollment.course?.title}</CardTitle>
                  </CardHeader>
                  
                  <CardContent className="flex-grow">
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Kursuse progress</span>
                          <span>{enrollment.progress}%</span>
                        </div>
                        <Progress value={enrollment.progress} className="h-2" />
                      </div>
                      
                      <div className="text-sm text-gray-700">
                        <div className="flex items-center mb-2">
                          <Play className="h-4 w-4 mr-2 text-kaija-accent" />
                          <span>Viimati vaadatud: {enrollment.last_lesson_title || "Esimene tund"}</span>
                        </div>
                        <div className="flex items-center">
                          <BookOpen className="h-4 w-4 mr-2 text-kaija-accent" />
                          <span>Lõpetatud: {enrollment.completed_lessons.length}/{enrollment.course?.lesson_count} tundi</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                  
                  <CardFooter className="mt-auto">
                    <Button 
                      className="w-full bg-kaija-accent hover:bg-opacity-90 text-white"
                      onClick={() => handleContinueCourse(enrollment.course_id)}
                    >
                      Jätka kursust
                    </Button>
                  </CardFooter>
                </Card>
              ))}
              
              <Card className="overflow-hidden flex flex-col h-full border-dashed">
                <div className="h-48 bg-gray-100 flex items-center justify-center">
                  <BookOpen className="h-12 w-12 text-gray-400" />
                </div>
                
                <CardHeader>
                  <CardTitle className="text-xl text-gray-700">Avasta rohkem kursuseid</CardTitle>
                </CardHeader>
                
                <CardContent className="flex-grow">
                  <p className="text-gray-600">
                    Leia endale sobiv kursus meie valikus ja jätka oma õpiteekonda.
                  </p>
                </CardContent>
                
                <CardFooter className="mt-auto">
                  <Button asChild className="w-full" variant="outline">
                    <Link to="/courses">Vaata kursuseid</Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>
          ) : (
            <div className="text-center py-12">
              <BookOpen className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <h2 className="text-2xl font-medium mb-3">Sul pole veel ühtegi kursust</h2>
              <p className="text-gray-700 mb-6 max-w-lg mx-auto">
                Alusta oma õpiteekonda meie mitmekesistest kursustest valides
              </p>
              <Button asChild className="bg-kaija-accent hover:bg-opacity-90 text-white">
                <Link to="/courses">Vaata kursuseid</Link>
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default MyCourses;
